import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Modal from 'tio-ui/components/modal';
import { fetchReportStats, type ReportStats } from '../utils/report-stats';
import type FeaturesService from 'tio-common/services/features';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';

export interface ReportStatsModalSignature {
  Args: {
    /** The report whose stats to show; null/undefined keeps the modal closed */
    report: Report | null | undefined;
    /** When set, stats are scoped to this company's viewers (partner listing) */
    companyId?: string;
    onClose: () => void;
  };
}

/**
 * Page-level modal that lazily fetches and shows per-report usage stats
 * (total views, unique users, exports, whether filters/sorts were used, and
 * a views-by-company breakdown). Backed by the /api/v2/report-trackings/stats
 * endpoint, which scopes data by visibility.
 *
 * Used by the admin listings (full, all-companies breakdown) and by the partner
 * listing with `@companyId` set, which scopes the stats to that company and
 * hides the cross-company breakdown.
 */
export default class ReportStatsModal extends Component<ReportStatsModalSignature> {
  @service declare features: FeaturesService;

  get isOpen(): boolean {
    return Boolean(this.args.report?.id);
  }

  statsData = trackedFunction(this, async () => {
    const id = this.args.report?.id;
    if (!id) return null;
    return fetchReportStats(id, this.features.ENV?.apiKey ?? '', this.args.companyId);
  });

  /** Hide the cross-company breakdown when scoped to a single company. */
  get showCompanyBreakdown(): boolean {
    return !this.args.companyId;
  }

  get stats(): ReportStats | null {
    return this.statsData.value ?? null;
  }

  get isLoading(): boolean {
    return this.statsData.isLoading;
  }

  get hasCompanyBreakdown(): boolean {
    return (this.stats?.views_by_company?.length ?? 0) > 0;
  }

  yesNo = (value: boolean | undefined) => (value ? 'Yes' : 'No');

  <template>
    <Modal @isOpen={{this.isOpen}} @onClose={{@onClose}} @size="md" @ariaLabel="Report usage stats">
      <:header>
        {{! @report is briefly null while the modal animates closed }}
        {{#if @report}}
          <span class="tio-fg-primary">Usage · {{@report.displayName}}</span>
        {{/if}}
      </:header>
      <:body>
        {{#if @report}}
          {{#if this.isLoading}}
            <div class="py-8 text-center text-sm tio-fg-muted">
              Loading stats…
            </div>
          {{else if this.stats}}
            <div class="space-y-5" data-test-report-stats>
              {{! Top metrics }}
              <div class="grid grid-cols-3 gap-3">
                <div class="rounded-lg border tio-border-default p-3 text-center">
                  <div class="tio-text-2xl-semibold tio-fg-primary" data-test-total-views>
                    {{this.stats.total_views}}
                  </div>
                  <div class="text-xs tio-fg-muted">Total views</div>
                </div>
                <div class="rounded-lg border tio-border-default p-3 text-center">
                  <div class="tio-text-2xl-semibold tio-fg-primary">
                    {{this.stats.unique_users}}
                  </div>
                  <div class="text-xs tio-fg-muted">Unique users</div>
                </div>
                <div class="rounded-lg border tio-border-default p-3 text-center">
                  <div class="tio-text-2xl-semibold tio-fg-primary">
                    {{this.stats.export_count}}
                  </div>
                  <div class="text-xs tio-fg-muted">Exports</div>
                </div>
              </div>

              {{! Activity booleans }}
              <div class="flex gap-6 text-sm">
                <div>
                  <span class="tio-fg-muted">Filters used:</span>
                  <span class="ml-1 font-medium tio-fg-primary">
                    {{this.yesNo this.stats.filters_used}}
                  </span>
                </div>
                <div>
                  <span class="tio-fg-muted">Sorts applied:</span>
                  <span class="ml-1 font-medium tio-fg-primary">
                    {{this.yesNo this.stats.sorts_used}}
                  </span>
                </div>
              </div>

              {{! Views by company (hidden when scoped to a single company) }}
              {{#if this.showCompanyBreakdown}}
                <div>
                  <div class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted mb-2">
                    Views by company
                  </div>
                  {{#if this.hasCompanyBreakdown}}
                    <div class="space-y-1">
                      {{#each this.stats.views_by_company as |row|}}
                        <div
                          class="tio-row-between text-sm border-b tio-border-subtle py-1 last:border-0"
                        >
                          <span class="tio-fg-secondary truncate">
                            {{row.company_name}}
                          </span>
                          <span class="font-medium tio-fg-primary ml-2">
                            {{row.views}}
                          </span>
                        </div>
                      {{/each}}
                    </div>
                  {{else}}
                    <div class="text-sm tio-fg-muted">No company data yet.</div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{else}}
            <div class="py-8 text-center text-sm tio-fg-muted">
              No usage data available for this report yet.
            </div>
          {{/if}}
        {{/if}}
      </:body>
    </Modal>
  </template>
}
